import { ApolloProvider } from '@apollo/client';

/*
 * Route *tables* are imported from their defining module rather than their
 * lib's barrel: the table is a handful of objects, while the barrel re-exports
 * the lib's entire surface and would pull all of it into the entry chunk. The
 * pages the tables point at are deferred inside those modules.
 */
import { createAdminRoutes } from '@eluve-lib/frontend-admin/router';
import { outputTemplateRoutes } from '@eluve-lib/frontend-feature-output-templates/output-template-routes';
import { createReviewRoutes } from '@eluve-lib/frontend-review-dash/routes';
import { ErrorBoundary } from '@sentry/react';
import { Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useParams,
} from 'react-router-dom';
import 'supertokens-auth-react';
import * as Session from 'supertokens-auth-react/recipe/session';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';

import { apolloClientFactory, defaultCache } from '@eluve/apollo-client';
import {
  TenantToOrganizationRedirect,
  lazyRoute,
} from '@eluve/app-router-utils';
import {
  BrowserCompatibilityGate,
  NotFoundBoundary,
  NotFoundPage,
} from '@eluve/blocks';
import { LoadingIndicator, Main } from '@eluve/components';
import {
  ErrorFallbackRender,
  LoginCallbackPage,
  LogoutPage,
  MagicLinkVerifyPage,
  SsoPage,
} from '@eluve/feature-login';
import {
  MaintenanceGate,
  SessionlessAnalyticsProvider,
  UrlParamNormalizer,
  getPostHogHostConfig,
} from '@eluve/posthog-react';

import { AnonymousIntercomProvider } from './IntercomProvider';
import { appConfig } from './config';
import { LoginPageWrapper } from './routes/LoginPageWrapper';
import { RemoveSegmentRedirect } from './routes/app/RemoveSegmentRedirect';
import { SingularEntityRedirect } from './routes/app/SingularEntityRedirect';
import { createEvalRoutes } from './routes/app/eval/eval-routes';
import { createPatientRoutes } from './routes/app/patient/patient-routes';
import {
  AdminToSettingsRedirect,
  OutputTemplatesToSettingsRedirect,
  TenantSettingsRedirect,
} from './routes/app/tenant/$tenantId/settings/settingsRedirects';
import { sessionActor, tokenProvider } from './session-token-provider';

const rootApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  cacheInstance: defaultCache,
  accessTokenProvider: tokenProvider,
});

const adminApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  desiredRole: 'eluve-admin',
  cacheInstance: defaultCache,
  accessTokenProvider: tokenProvider,
});

const reviewerApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  /**
   * Review dashboard is shared between tenant reviewers and ML engineers.
   * The `ml-engineer` Hasura role is a superset of `reviewer` (sees tenants
   * via either membership), so prefer it when the user is provisioned as
   * both — that way dual-role users see the union of their access.
   */
  desiredRole: async () => {
    try {
      const payload = (await Session.getAccessTokenPayloadSecurely()) as {
        isReviewer?: boolean;
        isMlEngineer?: boolean;
      };
      if (payload?.isMlEngineer) return 'ml-engineer';
      if (payload?.isReviewer) return 'reviewer';
    } catch {
      // fall through to session default role
    }
    return undefined;
  },
  cacheInstance: defaultCache,
  accessTokenProvider: tokenProvider,
});

const tenantScopedApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  cacheInstance: defaultCache,
  accessTokenProvider: tokenProvider,
});

const postHogHost = getPostHogHostConfig(
  appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_HOST,
  appConfig.VITE_POSTHOG_PROXY_HOST,
);

/*
 * Everything below is loaded on demand.
 *
 * The route table has to name every component it can render, which used to
 * mean `router.tsx` statically imported every page in the app — so an
 * unauthenticated visitor downloaded the reporting dashboard, the admin
 * console and the review dashboard before the login form could paint. Only the
 * unauthenticated surface (login, SSO, magic-link verify, logout) is imported
 * eagerly; every authenticated route names a `lazyRoute` and fetches its chunk
 * on first render. `router.code-splitting.spec.ts` guards the boundary.
 */

// Authenticated shells — one chunk each, see their modules for why.
const AuthenticatedAppShell = lazyRoute(
  'AuthenticatedAppShell',
  () => import('./routes/app/AuthenticatedAppShell'),
);
const TenantRouteShell = lazyRoute(
  'TenantRouteShell',
  () => import('./routes/app/tenant/$tenantId/TenantRouteShell'),
);

// Feature-flag and access gates used by the route table.
const ChatAccessGate = lazyRoute(
  'ChatAccessGate',
  () => import('@eluve/smart-blocks'),
);
const FeatureFlaggedPage = lazyRoute(
  'FeatureFlaggedPage',
  () => import('@eluve/smart-blocks'),
);
const PostHogFeatureFlaggedPage = lazyRoute(
  'PostHogFeatureFlaggedPage',
  () => import('@eluve/smart-blocks'),
);
const TenantOrUserFeatureFlaggedComponent = lazyRoute(
  'TenantOrUserFeatureFlaggedComponent',
  () => import('@eluve/smart-blocks'),
);
const CarePlanFeatureFlaggedPage = lazyRoute(
  'CarePlanFeatureFlaggedPage',
  () => import('./routes/app/tenant/CarePlanFeatureGate'),
);
const TenantAdminGuard = lazyRoute(
  'TenantAdminGuard',
  () => import('./routes/app/tenant/$tenantId/TenantAdminGuard'),
);
const TemplatesGuard = lazyRoute(
  'TemplatesGuard',
  () => import('@eluve/frontend-feature-output-templates'),
);
const PromptsGuard = lazyRoute(
  'PromptsGuard',
  () => import('@eluve/frontend-feature-prompt-templates'),
);

// Top-level pages outside the authenticated shell.
const ImpersonationLoginPage = lazyRoute(
  'ImpersonationLoginPage',
  () => import('./routes/ImpersonationLoginPage'),
);
const WaitListPage = lazyRoute(
  'WaitListPage',
  () => import('./routes/WaitlistPage'),
);

// Organization-level pages.
const AppIndexPage = lazyRoute(
  'AppIndexPage',
  () => import('./routes/app/AppIndexPage'),
);
const TenantsPage = lazyRoute(
  'TenantsPage',
  () => import('./routes/app/tenant/TenantsPage'),
);
const PendingApprovalPage = lazyRoute(
  'PendingApprovalPage',
  () => import('./routes/app/tenant/PendingApprovalPage'),
);
const TenantLayout = lazyRoute(
  'TenantLayout',
  () => import('./routes/app/tenant/$tenantId/TenantLayout'),
);
const HomePage = lazyRoute(
  'HomePage',
  () => import('./routes/app/tenant/$tenantId/HomePage'),
);
const ReportingDashboardPage = lazyRoute(
  'ReportingDashboardPage',
  () =>
    import('./routes/app/tenant/$tenantId/reporting/ReportingDashboardPage'),
);
const TasksListPage = lazyRoute(
  'TasksListPage',
  () => import('./routes/app/tenant/$tenantId/tasks/TasksListPage'),
);

// Appointments.
const AppointmentsMainLayout = lazyRoute(
  'AppointmentsMainLayout',
  () =>
    import('./routes/app/tenant/$tenantId/appointments/AppointmentsMainLayout'),
);
const AppointmentsListPage = lazyRoute(
  'AppointmentsListPage',
  () =>
    import('./routes/app/tenant/$tenantId/appointments/AppointmentsListPage'),
);
const AppointmentPage = lazyRoute(
  'AppointmentPage',
  () => import('./routes/app/tenant/$tenantId/appointments/Appointment.Page'),
);
const SummaryComparisonPage = lazyRoute(
  'SummaryComparisonPage',
  () =>
    import('./routes/app/tenant/$tenantId/appointments/SummaryComparison.Page'),
);
const AppointmentDetailsOutput = lazyRoute(
  'AppointmentDetailsOutput',
  () => import('@eluve/frontend-feature-appointment'),
);
const AppointmentDetailsPage = lazyRoute(
  'AppointmentDetailsPage',
  () => import('@eluve/frontend-feature-appointment'),
);
const AppointmentPdfPage = lazyRoute(
  'AppointmentPdfPage',
  () => import('@eluve/frontend-feature-appointment'),
);
const AppointmentFilesList = lazyRoute(
  'AppointmentFilesList',
  () => import('@eluve/user-local-files'),
);

// Settings.
const SettingsPage = lazyRoute(
  'SettingsPage',
  () => import('./routes/app/settings/SettingsPage'),
);
const SettingsShell = lazyRoute(
  'SettingsShell',
  () => import('./routes/app/tenant/$tenantId/settings/SettingsShell'),
);
const UserSettingsPage = lazyRoute(
  'UserSettingsPage',
  () => import('./routes/app/tenant/$tenantId/settings/pages/UserSettingsPage'),
);
const AudioDisplaySettingsPage = lazyRoute(
  'AudioDisplaySettingsPage',
  () =>
    import(
      './routes/app/tenant/$tenantId/settings/pages/AudioDisplaySettingsPage'
    ),
);
const ShortcutsSettingsPage = lazyRoute(
  'ShortcutsSettingsPage',
  () =>
    import(
      './routes/app/tenant/$tenantId/settings/pages/ShortcutsSettingsPage'
    ),
);
const AppGrantsSettingsPage = lazyRoute(
  'AppGrantsSettingsPage',
  () =>
    import(
      './routes/app/tenant/$tenantId/settings/pages/AppGrantsSettingsPage'
    ),
);
const NoteSettingsPage = lazyRoute(
  'NoteSettingsPage',
  () => import('./routes/app/tenant/$tenantId/settings/pages/NoteSettingsPage'),
);
const TaskSettingsPage = lazyRoute(
  'TaskSettingsPage',
  () => import('./routes/app/tenant/$tenantId/settings/pages/TaskSettingsPage'),
);
const ChatSettingsPage = lazyRoute(
  'ChatSettingsPage',
  () =>
    import(
      './routes/app/tenant/$tenantId/settings/pages/chat/ChatSettingsPage'
    ),
);
const ChatOutputPreferencesPage = lazyRoute(
  'ChatOutputPreferencesPage',
  () =>
    import(
      './routes/app/tenant/$tenantId/settings/pages/chat/ChatOutputPreferencesPage'
    ),
);
const ChatCustomInstructionsPage = lazyRoute(
  'ChatCustomInstructionsPage',
  () =>
    import(
      './routes/app/tenant/$tenantId/settings/pages/chat/ChatCustomInstructionsPage'
    ),
);
const ChatConversationStartersPage = lazyRoute(
  'ChatConversationStartersPage',
  () =>
    import(
      './routes/app/tenant/$tenantId/settings/pages/chat/ChatConversationStartersPage'
    ),
);
const TenantUserInsightsSettingsPage = lazyRoute(
  'TenantUserInsightsSettingsPage',
  () =>
    import(
      './routes/app/tenant/$tenantId/settings/insights/TenantUserInsightsSettingsPage'
    ),
);
const OrganizationGeneralPage = lazyRoute(
  'OrganizationGeneralPage',
  () =>
    import(
      './routes/app/tenant/$tenantId/settings/pages/OrganizationGeneralPage'
    ),
);
const OrganizationLayersPage = lazyRoute(
  'OrganizationLayersPage',
  () =>
    import(
      './routes/app/tenant/$tenantId/settings/pages/OrganizationLayersPage'
    ),
);
const UsersPage = lazyRoute(
  'UsersPage',
  () => import('@eluve/frontend-feature-tenants'),
);
const MemberDetailPage = lazyRoute(
  'MemberDetailPage',
  () => import('@eluve/frontend-feature-tenants'),
);
const SessionTypesPage = lazyRoute(
  'SessionTypesPage',
  () => import('@eluve/frontend-feature-tenants'),
);
const LocationsRoot = lazyRoute(
  'LocationsRoot',
  () => import('@eluve/frontend-feature-tenants'),
);
const LocationRoot = lazyRoute(
  'LocationRoot',
  () => import('@eluve/frontend-feature-tenants'),
);
const UserGroupsListPage = lazyRoute(
  'UserGroupsListPage',
  () => import('@eluve/frontend-feature-tenants'),
);
const UserGroupDetailsPage = lazyRoute(
  'UserGroupDetailsPage',
  () => import('@eluve/frontend-feature-tenants'),
);
const RegenerateNotesPage = lazyRoute(
  'RegenerateNotesPage',
  () => import('@eluve/frontend-feature-tenants'),
);

// Output templates and prompts.
const TenantTemplatesIndexPage = lazyRoute(
  'TenantTemplatesIndexPage',
  () => import('@eluve/frontend-feature-output-templates'),
);
const NewTemplateBundlePage = lazyRoute(
  'NewTemplateBundlePage',
  () => import('@eluve/frontend-feature-output-templates'),
);
const GenerateWithAiPage = lazyRoute(
  'GenerateWithAiPage',
  () => import('@eluve/frontend-feature-output-templates'),
);
const EditTemplateBundlePage = lazyRoute(
  'EditTemplateBundlePage',
  () => import('@eluve/frontend-feature-output-templates'),
);
const PromptsPage = lazyRoute(
  'PromptsPage',
  () => import('@eluve/frontend-feature-prompt-templates'),
);

// Care plans and care library.
const CarePlansListPage = lazyRoute(
  'CarePlansListPage',
  () => import('@eluve/feature-care-plans'),
);
const CarePlanDetailPage = lazyRoute(
  'CarePlanDetailPage',
  () => import('@eluve/feature-care-plans'),
);
const PlanBuilderPage = lazyRoute(
  'PlanBuilderPage',
  () => import('@eluve/feature-care-plans'),
);
const CareLibraryPage = lazyRoute(
  'CareLibraryPage',
  () => import('@eluve/feature-care-library'),
);
const CareLibraryDetailPage = lazyRoute(
  'CareLibraryDetailPage',
  () => import('@eluve/feature-care-library'),
);

// Patients.
const PatientsListPage = lazyRoute(
  'PatientsListPage',
  () => import('@eluve/feature-patients'),
);
const PatientProfileGate = lazyRoute(
  'PatientProfileGate',
  () => import('@eluve/feature-patients'),
);
const PatientProfileIndexRedirect = lazyRoute(
  'PatientProfileIndexRedirect',
  () => import('@eluve/feature-patients'),
);
const PatientProfileRouteGuard = lazyRoute(
  'PatientProfileRouteGuard',
  () => import('@eluve/feature-patients'),
);
const PatientProfileAppointmentsRoute = lazyRoute(
  'PatientProfileAppointmentsRoute',
  () => import('@eluve/feature-patients'),
);
const PatientOverviewTab = lazyRoute(
  'PatientOverviewTab',
  () => import('@eluve/feature-patients'),
);
const OverviewCardGroup = lazyRoute(
  'OverviewCardGroup',
  () => import('@eluve/feature-patients'),
);
const PatientDocumentsTab = lazyRoute(
  'PatientDocumentsTab',
  () => import('@eluve/feature-patients'),
);
const PatientArtifacts = lazyRoute(
  'PatientArtifacts',
  () => import('@eluve/feature-patients'),
);
const PatientDetailsImportedNotes = lazyRoute(
  'PatientDetailsImportedNotes',
  () => import('@eluve/feature-patients'),
);
const MedicalProfile = lazyRoute(
  'MedicalProfile',
  () => import('@eluve/feature-patients'),
);
const CareTeam = lazyRoute('CareTeam', () => import('@eluve/feature-patients'));
const Observations = lazyRoute(
  'Observations',
  () => import('@eluve/feature-patients'),
);
const PatientFiles = lazyRoute(
  'PatientFiles',
  () => import('@eluve/feature-patients'),
);
const PatientCarePlans = lazyRoute(
  'PatientCarePlans',
  () => import('@eluve/feature-patients'),
);
const PatientCarePlanDetail = lazyRoute(
  'PatientCarePlanDetail',
  () => import('@eluve/feature-patients'),
);

// FHIR-backed sections rendered inside the patient profile. Care Plans goes
// through the feature-lib panel, which wraps the FHIR section with its visit
// picker.
const PatientCarePlansPanel = lazyRoute(
  'PatientCarePlansPanel',
  () => import('@eluve/feature-care-plans'),
);
const PatientConditionsSection = lazyRoute(
  'PatientConditionsSection',
  () => import('@eluve/fhir-conditions'),
);
const PatientGoalsSection = lazyRoute(
  'PatientGoalsSection',
  () => import('@eluve/fhir-goals'),
);
const PatientObservationsSection = lazyRoute(
  'PatientObservationsSection',
  () => import('@eluve/fhir-observations'),
);
const PatientTodosSection = lazyRoute(
  'PatientTodosSection',
  () => import('@eluve/fhir-service-requests'),
);
const MyTasksSection = lazyRoute(
  'MyTasksSection',
  () => import('@eluve/fhir-tasks'),
);
const ServicesCatalogPage = lazyRoute(
  'ServicesCatalogPage',
  () => import('@eluve/fhir-services'),
);
const ServiceDetailPage = lazyRoute(
  'ServiceDetailPage',
  () => import('@eluve/fhir-services'),
);

const PatientNotFoundBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { patientId } = useParams();
  return (
    <NotFoundBoundary
      entityType="Patient"
      listPathTemplate="/organizations/:tenantId/patients"
      listLabel="Back to patients"
      resetKeys={[patientId]}
    >
      {children}
    </NotFoundBoundary>
  );
};

const AppointmentNotFoundBoundary: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { appointmentId } = useParams();
  return (
    <NotFoundBoundary
      entityType="Appointment"
      listPathTemplate="/organizations/:tenantId/appointments"
      listLabel="Back to appointments"
      resetKeys={[appointmentId]}
    >
      {children}
    </NotFoundBoundary>
  );
};

const routes = [
  {
    path: '/login',
    element: (
      <UrlParamNormalizer>
        <SessionlessAnalyticsProvider
          apiKey={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
          apiHost={postHogHost}
        >
          <MaintenanceGate>
            <AnonymousIntercomProvider>
              <LoginPageWrapper />
            </AnonymousIntercomProvider>
          </MaintenanceGate>
        </SessionlessAnalyticsProvider>
      </UrlParamNormalizer>
    ),
  },
  {
    path: '/sign-out',
    element: (
      <SessionlessAnalyticsProvider
        apiKey={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
        apiHost={postHogHost}
      >
        <LogoutPage medplumBaseUrl={appConfig.VITE_MEDPLUM_BASE_URL} />
      </SessionlessAnalyticsProvider>
    ),
  },
  {
    path: '/login/callback/*',
    element: <LoginCallbackPage />,
  },
  {
    path: '/impersonate/callback',
    element: (
      <SessionAuth>
        <ImpersonationLoginPage />
      </SessionAuth>
    ),
  },
  {
    path: '/sso',
    element: <SsoPage />,
  },
  {
    path: '/waitlist',
    element: <WaitListPage />,
  },
  {
    path: '/auth/verify',
    element: <MagicLinkVerifyPage />,
  },
  {
    path: '/',
    element: (
      // `showDialog` is false because it is a plain boolean and so cannot
      // exclude recoverable errors; `ErrorFallbackRender` calls
      // `showReportDialog` itself for the errors that warrant one. This
      // boundary now only catches the shell's own provider stack failing —
      // route render errors are caught inside the shell, below
      // `VersionChecker`, so a failure no longer stops version polling.
      <ErrorBoundary
        showDialog={false}
        fallback={(props) => (
          <ErrorFallbackRender {...props} surface="web-vite" />
        )}
      >
        <AuthenticatedAppShell
          client={rootApolloClient}
          postHogHost={postHogHost}
        />
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <AppIndexPage />,
      },
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      {
        path: 'tenant/*',
        element: <TenantToOrganizationRedirect />,
      },
      {
        path: 'tenants/*',
        element: <TenantToOrganizationRedirect />,
      },
      {
        path: 'organization/*',
        element: <SingularEntityRedirect entityName="organization" />,
      },
      {
        path: 'organizations/switch/*',
        element: <RemoveSegmentRedirect segment="switch" />,
      },
      {
        path: 'organizations',
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <TenantsPage />,
          },
          {
            path: 'pending-approval',
            element: <PendingApprovalPage />,
          },
          {
            path: ':tenantId',
            element: (
              <TenantRouteShell
                client={tenantScopedApolloClient}
                sessionActor={sessionActor}
              />
            ),
            children: [
              {
                path: 'reporting',
                element: (
                  <>
                    <Helmet>
                      <title>Reports | Eluve</title>
                    </Helmet>
                    <ReportingDashboardPage />
                  </>
                ),
              },
              {
                path: 'settings',
                element: <SettingsShell />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="user" replace />,
                  },
                  {
                    path: 'user',
                    element: <UserSettingsPage />,
                  },
                  {
                    path: 'audio',
                    element: <AudioDisplaySettingsPage />,
                  },
                  {
                    path: 'shortcuts',
                    element: <ShortcutsSettingsPage />,
                  },
                  {
                    path: 'app-grants',
                    element: <AppGrantsSettingsPage />,
                  },
                  {
                    path: 'note',
                    element: <NoteSettingsPage />,
                  },
                  {
                    path: 'tasks',
                    element: (
                      // Double gate matching the Clinical Tasks panel: tenant
                      // flag AND PostHog user flag.
                      <FeatureFlaggedPage flag="CLINICAL_TASKS">
                        <PostHogFeatureFlaggedPage flag="CLINICAL_TASKS">
                          <TaskSettingsPage />
                        </PostHogFeatureFlaggedPage>
                      </FeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'chat',
                    element: (
                      <ChatAccessGate>
                        <Outlet />
                      </ChatAccessGate>
                    ),
                    children: [
                      {
                        index: true,
                        element: <ChatSettingsPage />,
                      },
                      {
                        path: 'output-preferences',
                        element: <ChatOutputPreferencesPage />,
                      },
                      {
                        path: 'custom-instructions',
                        element: <ChatCustomInstructionsPage />,
                      },
                      {
                        path: 'conversation-starters',
                        element: <ChatConversationStartersPage />,
                      },
                    ],
                  },
                  {
                    path: 'insights',
                    element: (
                      <PostHogFeatureFlaggedPage flag="EXTENSION_PREVISIT_INSIGHTS">
                        <Main>
                          <TenantUserInsightsSettingsPage />
                        </Main>
                      </PostHogFeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'templates',
                    element: (
                      <TemplatesGuard>
                        <Helmet>
                          <title>Templates | Eluve</title>
                        </Helmet>
                        <Outlet />
                      </TemplatesGuard>
                    ),
                    children: [
                      {
                        index: true,
                        element: <TenantTemplatesIndexPage />,
                      },
                      {
                        element: (
                          <Main>
                            <Outlet />
                          </Main>
                        ),
                        children: [
                          {
                            path: 'new',
                            element: <NewTemplateBundlePage />,
                          },
                          {
                            path: 'new/ai',
                            element: <GenerateWithAiPage />,
                          },
                          {
                            path: 'bundles/:outputTemplateId',
                            element: <EditTemplateBundlePage />,
                          },
                          ...outputTemplateRoutes,
                        ],
                      },
                    ],
                  },
                  {
                    element: (
                      <TenantAdminGuard>
                        <Outlet />
                      </TenantAdminGuard>
                    ),
                    children: [
                      {
                        path: 'organization',
                        element: <OrganizationGeneralPage />,
                      },
                      {
                        path: 'organization/layers',
                        element: (
                          <FeatureFlaggedPage flag="LOCATIONS">
                            <OrganizationLayersPage />
                          </FeatureFlaggedPage>
                        ),
                      },
                      {
                        path: 'members',
                        element: <UsersPage />,
                      },
                      {
                        path: 'members/:userId',
                        element: <MemberDetailPage />,
                      },
                      {
                        path: 'session-types',
                        element: (
                          <FeatureFlaggedPage flag="SESSION_TYPES">
                            <SessionTypesPage />
                          </FeatureFlaggedPage>
                        ),
                      },
                      {
                        path: 'locations',
                        element: (
                          // The hub absorbed the Clinics page, which was gated
                          // on NEW_BOOKING_EXPERIENCE — admit that audience too
                          // so they don't lose clinic timezone authoring.
                          <FeatureFlaggedPage
                            flag="LOCATIONS"
                            orFlag="NEW_BOOKING_EXPERIENCE"
                          >
                            <Outlet />
                          </FeatureFlaggedPage>
                        ),
                        children: [
                          {
                            index: true,
                            element: <LocationsRoot />,
                          },
                          {
                            path: ':locationId',
                            element: <LocationRoot />,
                          },
                        ],
                      },
                      {
                        path: 'user-groups',
                        element: (
                          <FeatureFlaggedPage flag="USER_GROUPS">
                            <Outlet />
                          </FeatureFlaggedPage>
                        ),
                        children: [
                          {
                            index: true,
                            element: <UserGroupsListPage />,
                          },
                          {
                            path: ':userGroupId',
                            element: <UserGroupDetailsPage />,
                          },
                        ],
                      },
                      {
                        path: 'regenerate-notes',
                        element: (
                          <FeatureFlaggedPage flag="ENABLE_TENANT_ADMIN_REGENERATE_NOTE">
                            <RegenerateNotesPage />
                          </FeatureFlaggedPage>
                        ),
                      },
                      {
                        // Org-wide service catalog (HealthcareService CRUD).
                        path: 'services',
                        element: (
                          <FeatureFlaggedPage flag="MEDPLUM">
                            <Helmet>
                              <title>Services | Eluve</title>
                            </Helmet>
                            <Outlet />
                          </FeatureFlaggedPage>
                        ),
                        children: [
                          {
                            index: true,
                            element: <ServicesCatalogPage />,
                          },
                          {
                            path: ':serviceId',
                            element: <ServiceDetailPage />,
                          },
                        ],
                      },
                      {
                        path: 'care-plans',
                        element: (
                          <PostHogFeatureFlaggedPage flag="CARE_PLANS">
                            <FeatureFlaggedPage flag="MEDPLUM">
                              <Outlet />
                            </FeatureFlaggedPage>
                          </PostHogFeatureFlaggedPage>
                        ),
                        children: [
                          {
                            index: true,
                            element: <CarePlansListPage />,
                          },
                          {
                            path: 'new',
                            element: (
                              <PlanBuilderPage
                                baseUrl={appConfig.VITE_API_DOMAIN}
                              />
                            ),
                          },
                          {
                            path: ':planId',
                            element: <CarePlanDetailPage />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'chat-settings',
                element: <TenantSettingsRedirect to="chat" />,
              },
              {
                path: 'insights-settings',
                element: <TenantSettingsRedirect to="insights" />,
              },
              {
                path: 'admin/*',
                element: <AdminToSettingsRedirect />,
              },
              {
                path: 'output-templates/*',
                element: <OutputTemplatesToSettingsRedirect />,
              },
              {
                element: <TenantLayout />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="home" replace />,
                  },
                  {
                    path: 'home',
                    element: <HomePage />,
                  },
                  {
                    path: 'files',
                    element: (
                      <FeatureFlaggedPage flag="LOCAL_FILES_MANAGEMENT">
                        <Helmet>
                          <title>Files | Eluve</title>
                        </Helmet>
                        <AppointmentFilesList />
                      </FeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'patient/*',
                    element: <SingularEntityRedirect entityName="patient" />,
                  },
                  {
                    path: 'appointments',
                    element: (
                      <FeatureFlaggedPage flag="APPOINTMENTS_VIEW">
                        <Helmet>
                          <title>Appointments | Eluve</title>
                        </Helmet>
                        <AppointmentsListPage />
                      </FeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'patients',
                    element: (
                      <FeatureFlaggedPage flag="PATIENTS_VIEW">
                        <PatientsListPage />
                      </FeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'tasks',
                    element: (
                      // Double gate matching the Clinical Tasks panel: tenant
                      // flag AND PostHog user flag.
                      <FeatureFlaggedPage flag="CLINICAL_TASKS">
                        <PostHogFeatureFlaggedPage flag="CLINICAL_TASKS">
                          <Helmet>
                            <title>Tasks | Eluve</title>
                          </Helmet>
                          <TasksListPage />
                        </PostHogFeatureFlaggedPage>
                      </FeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'patients/:patientId',
                    element: (
                      <FeatureFlaggedPage flag="PATIENTS_VIEW">
                        <PatientNotFoundBoundary>
                          <PatientProfileGate
                            baseUrl={appConfig.VITE_API_DOMAIN}
                          />
                        </PatientNotFoundBoundary>
                      </FeatureFlaggedPage>
                    ),
                    children: [
                      {
                        index: true,
                        element: <PatientProfileIndexRedirect />,
                      },
                      {
                        path: 'overview',
                        element: (
                          <PatientProfileRouteGuard owner="profile">
                            <PatientOverviewTab>
                              <PatientCarePlansPanel />
                              <OverviewCardGroup title="Clinical record">
                                <PatientConditionsSection />
                                <PatientGoalsSection />
                                <PatientObservationsSection />
                              </OverviewCardGroup>
                              <OverviewCardGroup title="Action items">
                                <MyTasksSection />
                                <PatientTodosSection />
                              </OverviewCardGroup>
                            </PatientOverviewTab>
                          </PatientProfileRouteGuard>
                        ),
                      },
                      {
                        path: 'documents',
                        element: (
                          <PatientProfileRouteGuard owner="profile">
                            <PatientDocumentsTab />
                          </PatientProfileRouteGuard>
                        ),
                      },
                      {
                        path: 'artifacts',
                        element: (
                          <PatientProfileRouteGuard
                            owner="legacy"
                            redirectTo="../documents"
                          >
                            <TenantOrUserFeatureFlaggedComponent flag="INSIGHTS_CHAT">
                              <PatientArtifacts />
                            </TenantOrUserFeatureFlaggedComponent>
                          </PatientProfileRouteGuard>
                        ),
                      },
                      {
                        path: 'appointments',
                        element: <PatientProfileAppointmentsRoute />,
                      },
                      {
                        path: 'imported-notes',
                        element: (
                          <PatientProfileRouteGuard
                            owner="legacy"
                            redirectTo="../documents"
                          >
                            <PatientDetailsImportedNotes />
                          </PatientProfileRouteGuard>
                        ),
                      },
                      {
                        path: 'medical-profile',
                        element: (
                          <PatientProfileRouteGuard owner="legacy">
                            <MedicalProfile />
                          </PatientProfileRouteGuard>
                        ),
                        children: [
                          {
                            index: true,
                            element: <Navigate to="care-team" replace />,
                          },
                          {
                            path: 'care-team',
                            element: <CareTeam />,
                          },
                          {
                            path: 'observations',
                            element: <Observations />,
                          },
                          {
                            path: 'patient-uploads',
                            element: <PatientFiles />,
                          },
                        ],
                      },
                      {
                        path: 'care-plans',
                        element: (
                          <PatientProfileRouteGuard owner="legacy">
                            <PatientCarePlans />
                          </PatientProfileRouteGuard>
                        ),
                      },
                      {
                        path: 'care-plans/:carePlanId',
                        element: (
                          <PatientProfileRouteGuard owner="legacy">
                            <PatientCarePlanDetail />
                          </PatientProfileRouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'care-library',
                    element: (
                      <CarePlanFeatureFlaggedPage>
                        <Helmet>
                          <title>Care Library | Eluve</title>
                        </Helmet>
                        <CareLibraryPage />
                      </CarePlanFeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'care-library/:offeringId',
                    element: (
                      <CarePlanFeatureFlaggedPage>
                        <CareLibraryDetailPage />
                      </CarePlanFeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'appointment/*',
                    element: (
                      <SingularEntityRedirect entityName="appointment" />
                    ),
                  },
                  {
                    path: 'appointments/:appointmentId',
                    element: (
                      <AppointmentNotFoundBoundary>
                        <AppointmentsMainLayout />
                      </AppointmentNotFoundBoundary>
                    ),
                    children: [
                      {
                        index: true,
                        element: <AppointmentPage />,
                      },
                      {
                        path: 'comparison',
                        element: <SummaryComparisonPage />,
                      },
                      {
                        path: 'pdf',
                        element: (
                          <FeatureFlaggedPage flag="PDF_OUTPUT">
                            <AppointmentPdfPage />
                          </FeatureFlaggedPage>
                        ),
                      },
                      {
                        path: 'details',
                        element: (
                          <FeatureFlaggedPage flag="LLM_OUTPUT_DETAILS">
                            <AppointmentDetailsPage />
                          </FeatureFlaggedPage>
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <div className="text-text-tertiary">
                                Select Output types
                              </div>
                            ),
                          },
                          {
                            path: ':outputType',
                            element: <AppointmentDetailsOutput />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'prompts',
        element: (
          <PromptsGuard>
            <ApolloProvider client={adminApolloClient}>
              <PromptsPage />
            </ApolloProvider>
          </PromptsGuard>
        ),
      },
      ...createAdminRoutes({
        path: 'admin',
        client: adminApolloClient,
        tenantScopedApolloClient,
        sessionActor,
        postHogProjectId: appConfig.VITE_POSTHOG_PROJECT_ID,
        langfuseBaseUrl: appConfig.VITE_LANGFUSE_BASE_URL,
        langfuseProjectId: appConfig.VITE_LANGFUSE_PROJECT_ID,
      }),
      ...createEvalRoutes('eval'),
      ...createReviewRoutes('review', reviewerApolloClient),
    ],
  },
  ...createPatientRoutes('/patient-organization', rootApolloClient),
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export const reactRouter = createBrowserRouter([
  {
    /**
     * Outermost suspense boundary. Routes that own a more specific fallback
     * (the tenant shell, eval, patient intake) still resolve against theirs —
     * this one only catches lazy routes that have nothing closer.
     *
     * The error boundary is the equivalent backstop. Without it, the lazy
     * routes that sit above the authenticated shell — `/waitlist`,
     * `/impersonate/callback` and patient intake — fall through to
     * react-router's `defaultErrorElement`, an unstyled page reading
     * "Unexpected Application Error!". Nested boundaries still win for the
     * routes that have one.
     */
    element: (
      <ErrorBoundary
        showDialog={false}
        fallback={(props) => (
          <ErrorFallbackRender {...props} surface="web-vite" />
        )}
      >
        <Suspense fallback={<LoadingIndicator size="xl" />}>
          <BrowserCompatibilityGate />
        </Suspense>
      </ErrorBoundary>
    ),
    children: routes,
  },
]);
